import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';

const getAuthHeaders = () => ({
  authorization: localStorage.getItem('access_token') || '',
});

const initialState = {
  members: [],
  candidates: [],
  membersLoading: false,
  candidatesLoading: false,
  actionLoading: false,
  error: null,
};

export const fetchAdminTeamMembers = createAsyncThunk(
  'adminTeam/fetchMembers',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URI}/admin/team/members`,
        {
          headers: getAuthHeaders(),
        }
      );

      return response.data.data || [];
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const searchAdminTeamCandidates = createAsyncThunk(
  'adminTeam/searchCandidates',
  async ({ search = '', limit = 15 } = {}, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URI}/admin/team/candidates`,
        {
          params: { search, limit },
          headers: getAuthHeaders(),
        }
      );

      return response.data.data || [];
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const saveAdminTeamMember = createAsyncThunk(
  'adminTeam/saveMember',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URI}/admin/team/members`,
        payload,
        {
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json',
          },
        }
      );

      return {
        data: response.data.data,
        message: response.data.message,
        inviteEmailSent: response.data.inviteEmailSent,
        inviteEmailError: response.data.inviteEmailError,
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateAdminTeamMemberRole = createAsyncThunk(
  'adminTeam/updateRole',
  async ({ memberId, role }, { rejectWithValue }) => {
    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_BACKEND_URI}/admin/team/members/${memberId}/role`,
        { role },
        {
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json',
          },
        }
      );

      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const disableAdminTeamMember = createAsyncThunk(
  'adminTeam/disableMember',
  async (memberId, { rejectWithValue }) => {
    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_BACKEND_URI}/admin/team/members/${memberId}/disable`,
        {},
        {
          headers: getAuthHeaders(),
        }
      );

      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const activateAdminTeamMember = createAsyncThunk(
  'adminTeam/activateMember',
  async (memberId, { rejectWithValue }) => {
    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_BACKEND_URI}/admin/team/members/${memberId}/activate`,
        {},
        {
          headers: getAuthHeaders(),
        }
      );

      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const resendAdminTeamInvite = createAsyncThunk(
  'adminTeam/resendInvite',
  async (memberId, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URI}/admin/team/members/${memberId}/resend-invite`,
        {},
        {
          headers: getAuthHeaders(),
        }
      );

      return {
        data: response.data.data,
        message: response.data.message,
        inviteEmailSent: response.data.inviteEmailSent,
        inviteEmailError: response.data.inviteEmailError,
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteAdminTeamMember = createAsyncThunk(
  'adminTeam/deleteMember',
  async (memberId, { rejectWithValue }) => {
    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_BACKEND_URI}/admin/team/members/${memberId}`,
        {
          headers: getAuthHeaders(),
        }
      );

      return {
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const upsertMember = (members, nextMember) => {
  const existingIndex = members.findIndex(member => member.id === nextMember.id);
  if (existingIndex === -1) {
    return [nextMember, ...members];
  }

  const updatedMembers = [...members];
  updatedMembers[existingIndex] = nextMember;
  return updatedMembers;
};

const removeMember = (members, memberId) =>
  members.filter(member => member.id !== memberId);

export const adminTeamSlice = createSlice({
  name: 'adminTeamSlice',
  initialState,
  reducers: {
    clearAdminTeamError: state => {
      state.error = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchAdminTeamMembers.pending, state => {
        state.membersLoading = true;
        state.error = null;
      })
      .addCase(fetchAdminTeamMembers.fulfilled, (state, action) => {
        state.membersLoading = false;
        state.members = action.payload;
      })
      .addCase(fetchAdminTeamMembers.rejected, (state, action) => {
        state.membersLoading = false;
        state.error = action.payload || action.error.message;
      })
      .addCase(searchAdminTeamCandidates.pending, state => {
        state.candidatesLoading = true;
        state.error = null;
      })
      .addCase(searchAdminTeamCandidates.fulfilled, (state, action) => {
        state.candidatesLoading = false;
        state.candidates = action.payload;
      })
      .addCase(searchAdminTeamCandidates.rejected, (state, action) => {
        state.candidatesLoading = false;
        state.error = action.payload || action.error.message;
      })
      .addCase(saveAdminTeamMember.pending, state => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(saveAdminTeamMember.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.members = upsertMember(state.members, action.payload.data);
      })
      .addCase(saveAdminTeamMember.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload || action.error.message;
      })
      .addCase(updateAdminTeamMemberRole.pending, state => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(updateAdminTeamMemberRole.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.members = upsertMember(state.members, action.payload);
      })
      .addCase(updateAdminTeamMemberRole.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload || action.error.message;
      })
      .addCase(disableAdminTeamMember.pending, state => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(disableAdminTeamMember.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.members = upsertMember(state.members, action.payload);
      })
      .addCase(disableAdminTeamMember.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload || action.error.message;
      })
      .addCase(activateAdminTeamMember.pending, state => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(activateAdminTeamMember.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.members = upsertMember(state.members, action.payload);
      })
      .addCase(activateAdminTeamMember.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload || action.error.message;
      })
      .addCase(resendAdminTeamInvite.pending, state => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(resendAdminTeamInvite.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.members = upsertMember(state.members, action.payload.data);
      })
      .addCase(resendAdminTeamInvite.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload || action.error.message;
      })
      .addCase(deleteAdminTeamMember.pending, state => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(deleteAdminTeamMember.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.members = removeMember(state.members, action.payload.data.id);
      })
      .addCase(deleteAdminTeamMember.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload || action.error.message;
      });
  },
});

export const { clearAdminTeamError } = adminTeamSlice.actions;

export default adminTeamSlice.reducer;
